import { useEffect, useMemo, useState } from "react";
import { Activity, ShieldAlert } from "lucide-react";
import { Seo } from "@/components/Seo";
import { DemoBanner } from "@/components/portal/DemoBanner";
import { Badge } from "@/components/ui/badge";
import { CardSkeleton, FeedbackMessage } from "@/components/ui/feedback";
import { Label } from "@/components/ui/input";
import { canViewActivityLog } from "@/features/auth/types";
import { useAuth } from "@/features/auth/useAuth";
import { listAuditLogs, type AuditLogEntry } from "@/features/audit/repo";

const actionLabel: Record<string, string> = {
  "user.create": "User created",
  "user.update": "User updated",
  "user.delete": "User deleted",
  "application.status_update": "Application status changed",
  "application.note_update": "Application notes saved",
  "application.note_create": "Application note added",
  "application.note_delete": "Application note deleted",
  "application.forward": "Application forwarded",
  "application.delete": "Application deleted",
  "message.status_update": "Message status changed",
  "message.note_update": "Message notes saved",
  "message.note_create": "Message note added",
  "message.note_delete": "Message note deleted",
  "load.note_update": "Load notes saved",
  "fleet.status_update": "Fleet status changed",
  "fleet.note_update": "Fleet notes saved",
  "fleet.update": "Fleet record updated",
  "job.create": "Job posting created",
  "job.update": "Job posting updated",
  "job.publish": "Job posting published",
  "job.unpublish": "Job posting unpublished",
  "job.delete": "Job posting deleted",
  "document.status_update": "Document status changed",
  "document.note_update": "Document notes saved",
};

const actionTone: Record<string, "default" | "accent" | "warning" | "danger" | "success"> = {
  "user.create": "success",
  "user.update": "default",
  "user.delete": "danger",
  "application.status_update": "accent",
  "application.note_update": "default",
  "application.note_create": "default",
  "application.note_delete": "danger",
  "application.forward": "warning",
  "application.delete": "danger",
  "message.status_update": "accent",
  "message.note_update": "default",
  "message.note_create": "default",
  "message.note_delete": "danger",
  "load.note_update": "default",
  "fleet.status_update": "accent",
  "fleet.note_update": "default",
  "fleet.update": "default",
  "job.create": "success",
  "job.update": "default",
  "job.publish": "success",
  "job.unpublish": "warning",
  "job.delete": "danger",
  "document.status_update": "accent",
  "document.note_update": "default",
};

const statusLabel: Record<string, string> = {
  new: "New",
  in_review: "In review",
  approved: "Approved",
  rejected: "Rejected",
  booked: "Booked",
  dispatched: "Dispatched",
  in_transit: "In transit",
  out_for_delivery: "Out for delivery",
  delivered: "Delivered",
  exception: "Exception",
  active: "Active",
  maintenance: "Maintenance",
  out_of_service: "Out of service",
};

const roleLabel: Record<string, string> = {
  customer: "Customer",
  driver: "Driver",
  carrier: "Owner-operator",
  internal: "Staff",
};

const staffLevelLabel: Record<string, string> = {
  admin: "Admin",
  manager: "Manager",
  member: "Member",
};

const departmentLabel: Record<string, string> = {
  dispatch: "Dispatch",
  safety: "Safety",
  maintenance: "Maintenance",
  accounting: "Accounting",
  hr: "HR",
};

const BB_TIME_ZONE = "America/Chicago";
const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

const timeFormat: Intl.DateTimeFormatOptions = {
  month: "short",
  day: "numeric",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
  timeZoneName: "short",
};

function fmtInTimeZone(iso: string, timeZone: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      ...timeFormat,
      timeZone,
    });
  } catch {
    return new Date(iso).toLocaleString(undefined, timeFormat);
  }
}

function fmtDayHeading(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    weekday: "long",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function text(value: unknown): string {
  return typeof value === "string" && value.trim() ? value : "";
}

function human(value: unknown): string {
  const raw = text(value);
  if (!raw) return "";
  return statusLabel[raw] ?? roleLabel[raw] ?? staffLevelLabel[raw] ?? departmentLabel[raw] ?? raw;
}

function statusChange(meta: Record<string, unknown>): string {
  const previous = text(meta.previousStatus);
  const next = text(meta.nextStatus);
  if (!next) return "";
  if (!previous || previous === next) return `Current status: ${human(next)}`;
  return `Status changed from ${human(previous)} to ${human(next)}`;
}

function compactDetails(items: Array<string | false>): string[] {
  return items.filter((item): item is string => Boolean(item));
}

function localDateKey(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function matchesDateFilter(
  iso: string,
  filter: "all" | "today" | "7d" | "30d" | "day",
  selectedDate: string,
): boolean {
  if (filter === "all") return true;
  const eventDate = new Date(iso);
  const now = new Date();

  if (filter === "today") {
    return localDateKey(eventDate) === localDateKey(now);
  }

  if (filter === "day") {
    if (!selectedDate) return true;
    return localDateKey(eventDate) === selectedDate;
  }

  const days = filter === "7d" ? 7 : 30;
  const start = startOfLocalDay(now);
  start.setDate(start.getDate() - (days - 1));
  return eventDate >= start;
}

export default function InternalActivityLog() {
  const { user } = useAuth();
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [filter, setFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState<"all" | "today" | "7d" | "30d" | "day">("all");
  const [selectedDate, setSelectedDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!canViewActivityLog(user)) {
      setLoading(false);
      return;
    }
    setLoading(true);
    listAuditLogs()
      .then((rows) => {
        setLogs(rows);
        setError(null);
      })
      .catch((e) => setError(e instanceof Error ? e.message : "Could not load activity log."))
      .finally(() => setLoading(false));
  }, [user]);

  const actions = useMemo(
    () => ["all", ...Array.from(new Set(logs.map((log) => log.action)))],
    [logs],
  );
  const visible = useMemo(() => {
    return logs.filter((log) => {
      if (filter !== "all" && log.action !== filter) return false;
      if (!matchesDateFilter(log.createdAt, dateFilter, selectedDate)) return false;
      return true;
    });
  }, [dateFilter, filter, logs, selectedDate]);
  const groupedVisible = useMemo(() => {
    const groups = new Map<string, AuditLogEntry[]>();
    for (const log of visible) {
      const key = localDateKey(new Date(log.createdAt));
      groups.set(key, [...(groups.get(key) ?? []), log]);
    }
    return [...groups.entries()].map(([day, entries]) => ({ day, entries }));
  }, [visible]);

  if (!canViewActivityLog(user)) {
    return (
      <>
        <Seo title="Activity log" noindex />
        <h1 className="text-2xl font-bold">Activity log</h1>
        <p className="mt-4 rounded-lg border border-dashed border-brand-200 p-6 text-sm text-brand-500">
          Activity logs are available to admins and department managers.
        </p>
      </>
    );
  }

  return (
    <>
      <Seo title="Activity log" noindex />
      <DemoBanner />

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold">
            <Activity className="h-6 w-6 text-accent-600" />
            Activity log
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-brand-500">
            Review sensitive actions across the portal. Admins see all actions;
            department managers see actions scoped to their department. Each card
            shows the action time in BB company time and, when available, the actor's
            local timezone.
          </p>
        </div>
        <div className="rounded-lg border border-brand-100 px-4 py-3 text-center text-xs">
          <div className="text-2xl font-bold text-brand-900">{logs.length}</div>
          Visible events
        </div>
      </div>

      {error && (
        <FeedbackMessage tone="error" className="mt-4">
          {error}
        </FeedbackMessage>
      )}

      <div className="mt-6 grid gap-3 sm:grid-cols-3">
        <div className="w-full max-w-sm">
          <Label htmlFor="activity-filter">Action type</Label>
          <select
            id="activity-filter"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="mt-1 flex h-10 w-full rounded-md border border-brand-200 bg-white px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500"
          >
            {actions.map((action) => (
              <option key={action} value={action}>
                {action === "all" ? "All actions" : actionLabel[action] ?? action}
              </option>
            ))}
          </select>
        </div>
        <div className="w-full max-w-sm">
          <Label htmlFor="activity-date-filter">Date range</Label>
          <select
            id="activity-date-filter"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value as typeof dateFilter)}
            className="mt-1 flex h-10 w-full rounded-md border border-brand-200 bg-white px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500"
          >
            <option value="all">All dates</option>
            <option value="today">Today</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="day">Specific day</option>
          </select>
        </div>
        {dateFilter === "day" && (
          <div className="w-full max-w-sm">
            <Label htmlFor="activity-specific-date">Specific day</Label>
            <input
              id="activity-specific-date"
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="mt-1 flex h-10 w-full rounded-md border border-brand-200 bg-white px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500"
            />
          </div>
        )}
        {(filter !== "all" || dateFilter !== "all") && (
          <p className="self-end pb-2 text-sm text-brand-500">
            Showing {visible.length} of {logs.length}
          </p>
        )}
      </div>

      <div className="mt-6 grid gap-3">
        {loading ? (
          <>
            <CardSkeleton />
            <CardSkeleton />
            <CardSkeleton />
          </>
        ) : visible.length === 0 ? (
          <p className="rounded-lg border border-dashed border-brand-200 p-6 text-center text-sm text-brand-500">
            No activity matches this filter yet.
          </p>
        ) : (
          groupedVisible.map((group) => (
            <section key={group.day} className="grid gap-3">
              <div className="sticky top-0 z-10 -mx-1 bg-brand-50/90 px-1 py-1 text-xs font-semibold uppercase tracking-wide text-brand-400 backdrop-blur">
                {fmtDayHeading(group.entries[0].createdAt)}
              </div>
              {group.entries.map((log) => (
                <AuditCard key={log.id} log={log} />
              ))}
            </section>
          ))
        )}
      </div>
    </>
  );
}

function AuditCard({ log }: { log: AuditLogEntry }) {
  const meta = log.meta ?? {};
  const actor = text(meta.actorName) || text(meta.actorEmail) || "Unknown user";
  const department =
    text(meta.targetDepartment) ||
    text(meta.affectedDepartment) ||
    text(meta.assignedToDepartment) ||
    text(meta.actorDepartment);
  const copy = describeAudit(log, actor);

  return (
    <article className="min-w-0 rounded-xl border border-brand-100 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={actionTone[log.action] ?? "default"}>
              {actionLabel[log.action] ?? log.action}
            </Badge>
            {department && <Badge variant="default">{department}</Badge>}
          </div>
          <p className="mt-2 break-words font-medium text-brand-900">{copy.title}</p>
          <p className="mt-1 break-words text-sm text-brand-600">{copy.summary}</p>
          <TimeDetails log={log} />
        </div>
        <ShieldAlert className="h-5 w-5 text-brand-300" />
      </div>
      {copy.details.length > 0 && (
        <div className="mt-3 grid gap-1 text-sm text-brand-600">
          {copy.details.map((detail) => (
            <p key={detail} className="break-words">{detail}</p>
          ))}
        </div>
      )}
    </article>
  );
}

function TimeDetails({ log }: { log: AuditLogEntry }) {
  const actorTimeZone = text(log.meta?.actorTimeZone);
  const showViewerTime = localTimeZone !== actorTimeZone && localTimeZone !== BB_TIME_ZONE;

  return (
    <details className="mt-2 text-xs text-brand-500">
      <summary className="cursor-pointer select-none hover:text-brand-800">
        Time details
      </summary>
      <div className="mt-2 rounded-lg bg-brand-50 p-3 text-brand-600">
        <div className="grid gap-1 sm:grid-cols-2">
          <TimeRow label="BB company time" value={fmtInTimeZone(log.createdAt, BB_TIME_ZONE)} />
          {actorTimeZone ? (
            <TimeRow
              label={`Actor local time (${actorTimeZone})`}
              value={fmtInTimeZone(log.createdAt, actorTimeZone)}
            />
          ) : (
            <TimeRow
              label="Actor local time"
              value="Not captured for this older event"
            />
          )}
          {showViewerTime && (
            <TimeRow label={`Your local time (${localTimeZone})`} value={fmtInTimeZone(log.createdAt, localTimeZone)} />
          )}
          <TimeRow label="UTC" value={fmtInTimeZone(log.createdAt, "UTC")} />
        </div>
      </div>
    </details>
  );
}

function TimeRow({ label, value }: { label: string; value: string }) {
  return (
    <p className="break-words">
      <span className="font-medium text-brand-800">{label}:</span> {value}
    </p>
  );
}

function describeAudit(
  log: AuditLogEntry,
  actor: string,
): { title: string; summary: string; details: string[] } {
  const meta = log.meta ?? {};
  const affectedUser = text(meta.affectedEmail) || text(meta.affectedUserId);
  const application = text(meta.applicationName);
  const message = text(meta.messageName);
  const job = text(meta.jobTitle);
  const load = text(meta.loadReference);
  const fleetUnit = text(meta.fleetUnit);
  const documentKind = human(meta.documentKind);
  const documentReference = text(meta.documentReference);
  const deptChange =
    text(meta.nextDepartment) &&
    `Department changed from ${human(meta.previousDepartment) || "not set"} to ${human(meta.nextDepartment)}`;
  const roleChange =
    text(meta.nextRole) &&
    `Role changed from ${human(meta.previousRole) || "not set"} to ${human(meta.nextRole)}`;
  const levelChange =
    text(meta.nextStaffLevel) &&
    `Staff level changed from ${human(meta.previousStaffLevel) || "not set"} to ${human(meta.nextStaffLevel)}`;

  switch (log.action) {
    case "user.create":
      return {
        title: `${actor} created a login account`,
        summary: affectedUser
          ? `Created ${affectedUser} as ${human(meta.affectedRole) || "a user"}.`
          : "A new user account was created.",
        details: compactDetails([
          text(meta.affectedDepartment) && `Department: ${human(meta.affectedDepartment)}`,
          text(meta.staffLevel) && `Staff level: ${human(meta.staffLevel)}`,
        ]),
      };
    case "user.update":
      return {
        title: `${actor} updated a user account`,
        summary: affectedUser ? `Updated settings for ${affectedUser}.` : "Updated a user account.",
        details: compactDetails([roleChange, levelChange, deptChange]),
      };
    case "user.delete":
      return {
        title: `${actor} deleted a user account`,
        summary: affectedUser
          ? `Removed the login for ${affectedUser}.`
          : "Removed a user login from the system.",
        details: compactDetails([
          text(meta.affectedRole) && `Deleted role: ${human(meta.affectedRole)}`,
          text(meta.affectedDepartment) && `Department: ${human(meta.affectedDepartment)}`,
          text(meta.affectedStaffLevel) && `Staff level: ${human(meta.affectedStaffLevel)}`,
        ]),
      };
    case "application.status_update":
      return {
        title: `${actor} changed an application status`,
        summary: application ? `Applicant: ${application}.` : "An application was updated.",
        details: compactDetails([
          text(meta.applicationType) && `Application type: ${human(meta.applicationType)}`,
          statusChange(meta),
        ]),
      };
    case "application.note_update":
    case "application.note_create":
      return {
        title: `${actor} saved application notes`,
        summary: application ? `Applicant: ${application}.` : "Internal notes were saved on an application.",
        details: compactDetails([
          text(meta.applicationType) && `Application type: ${human(meta.applicationType)}`,
        ]),
      };
    case "application.note_delete":
      return {
        title: `${actor} deleted their application note`,
        summary: application ? `Applicant: ${application}.` : "An internal application note was deleted.",
        details: compactDetails([
          text(meta.applicationType) && `Application type: ${human(meta.applicationType)}`,
        ]),
      };
    case "application.forward":
      return {
        title: `${actor} forwarded an application`,
        summary: application ? `Applicant: ${application}.` : "An application was forwarded.",
        details: compactDetails([
          text(meta.assignedToName) && `Forwarded to: ${text(meta.assignedToName)}`,
          text(meta.assignedToEmail) && `Forwarded user email: ${text(meta.assignedToEmail)}`,
          text(meta.assignedToDepartment) && `Forwarded department: ${human(meta.assignedToDepartment)}`,
        ]),
      };
    case "application.delete":
      return {
        title: `${actor} permanently deleted an application`,
        summary: application
          ? `Applicant record for ${application} was removed.`
          : "An applicant record was permanently removed.",
        details: compactDetails([
          text(meta.applicationType) && `Application type: ${human(meta.applicationType)}`,
        ]),
      };
    case "message.status_update":
      return {
        title: `${actor} changed a contact message status`,
        summary: message ? `Contact message from ${message}.` : "A contact message was updated.",
        details: compactDetails([statusChange(meta)]),
      };
    case "message.note_update":
    case "message.note_create":
      return {
        title: `${actor} saved contact message notes`,
        summary: message ? `Contact message from ${message}.` : "Internal notes were saved on a message.",
        details: compactDetails([statusChange(meta)]),
      };
    case "message.note_delete":
      return {
        title: `${actor} deleted their contact message note`,
        summary: message ? `Contact message from ${message}.` : "An internal message note was deleted.",
        details: compactDetails([statusChange(meta)]),
      };
    case "load.note_update":
      return {
        title: `${actor} saved dispatch notes`,
        summary: load ? `Load ${load} was updated with internal notes.` : "Dispatch notes were saved.",
        details: compactDetails([]),
      };
    case "fleet.status_update":
      return {
        title: `${actor} changed a fleet unit status`,
        summary: fleetUnit ? `Unit ${fleetUnit} was updated.` : "A fleet record was updated.",
        details: compactDetails([text(meta.nextStatus) && `Current status: ${human(meta.nextStatus)}`]),
      };
    case "fleet.note_update":
    case "fleet.update":
      return {
        title: `${actor} saved fleet notes`,
        summary: fleetUnit ? `Unit ${fleetUnit} was updated.` : "Fleet notes were saved.",
        details: compactDetails([text(meta.nextStatus) && `Current status: ${human(meta.nextStatus)}`]),
      };
    case "job.create":
      return {
        title: `${actor} created a job posting`,
        summary: job ? `Job: ${job}.` : "A new job posting was created.",
        details: compactDetails([text(meta.jobSlug) && `Posting URL slug: ${text(meta.jobSlug)}`]),
      };
    case "job.update":
      return {
        title: `${actor} updated a job posting`,
        summary: job ? `Job: ${job}.` : "A job posting was updated.",
        details: compactDetails([deptChange]),
      };
    case "job.publish":
      return {
        title: `${actor} published a job posting`,
        summary: job ? `${job} is now visible to applicants.` : "A job posting is now visible.",
        details: compactDetails([]),
      };
    case "job.unpublish":
      return {
        title: `${actor} unpublished a job posting`,
        summary: job ? `${job} is no longer visible to applicants.` : "A job posting was hidden.",
        details: compactDetails([]),
      };
    case "job.delete":
      return {
        title: `${actor} deleted a job posting`,
        summary: job ? `Removed job posting: ${job}.` : "A job posting was deleted.",
        details: compactDetails([]),
      };
    case "document.status_update":
      return {
        title: `${actor} changed a document review status`,
        summary: documentKind ? `Document type: ${documentKind}.` : "A document review was updated.",
        details: compactDetails([documentReference && `Reference: ${documentReference}`, statusChange(meta)]),
      };
    case "document.note_update":
      return {
        title: `${actor} saved document review notes`,
        summary: documentKind ? `Document type: ${documentKind}.` : "Document notes were saved.",
        details: compactDetails([documentReference && `Reference: ${documentReference}`]),
      };
    default:
      return {
        title: actionLabel[log.action] ?? "Activity recorded",
        summary: `${actor} performed an action in the portal.`,
        details: compactDetails([]),
      };
  }
}
